// ============================================================================
// « PRIX & MARGES » — tout le catalogue en une vue : coût, prix, marge, et l'écart au coefficient
// convenu avec chaque fournisseur (maquette `isPricing`, relevé 2026-08-29).
//
// ⚠️ AUCUN CALCUL ICI. Marge, coefficient, HT et prix d'achat théorique du corner viennent de
// `prix-derive.ts` — les MÊMES fonctions que la fiche article et l'assistant Gamme. L'audit du module
// Articles (2026-08-25) a relevé TROIS définitions concurrentes de la marge et DEUX du coefficient :
// une définition de plus sur l'écran dont c'est le sujet serait la pire de toutes.
//
// ⚠️ LA CIBLE VIENT DU SERVEUR (`supplierCoefficientBp`), JAMAIS D'UN RAPPROCHEMENT À L'ÉCRAN. La
// première version rapprochait le libellé BRUT de l'article du libellé du référentiel : « TEXTIL
// NORD » et « Textil Nord » ne se trouvaient pas, et la cible disparaissait en silence. La base
// joint par CODE NORMALISÉ, avec la précédence de la fiche (la clause de la MARQUE surcharge celle
// du fournisseur) — une seule définition de « la cible ».
//
// ⚠️ LE RÉGIME CHANGE LE SENS DES COLONNES, comme à la fiche : en FERME les deux prix se saisissent
// et le coefficient se DÉRIVE ; en CORNER le coefficient EST le contrat, et c'est le prix d'achat
// qui s'en déduit (théorique, marqué « ≈ », jamais saisissable ici).
//
// ⚠️ LA FICHE S'OUVRE DANS L'ÉCRAN (`ArticleSheet` monté ici, son hôte prévu ce repli). La première
// version naviguait vers `/articles?model=…` — un paramètre que la liste NE LIT PAS : le bouton
// « ouvrir la fiche » ouvrait la liste, fiche fermée, sans une erreur pour le dire.
// ============================================================================
import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon, showToast, Trans } from '@orgazone/ui-web';
import { formatMinor, formatMoney } from '@orgazone/money-format';
import { api, type ArticleModel, type MarqueDuReferentiel, type ReferentialSupplier } from '../api';
import { ArticleSheet, type ArticleSheetLine } from './article-fiche';
import { annoncerCoefficient, ChampCoef, coefAffiche, coeffInput } from './champ-coefficient';
import { fineEnCentimes, ttcFromCoeffBp } from './prix-derive';
import { ecartALaCible, indicateursLigne, litSaisie, metaDe } from './prix-grille';
import { PanneauMassePrix } from './prix-masse';
import { fieldLabel } from '../vocabulary';
import { t } from '../i18n';

/** Combien de lignes la grille montre — la maquette en pose 60, puis dit ce qu'elle ne montre pas. */
const LIGNES_MAX = 60;

// Formats d'affichage — tout passe par money-format, aucun flottant maison (le « ÷100 puis toFixed »
// est exactement le genre d'arrondi parallèle que cet écran existe pour éliminer).
// ⚠️ LE SYMBOLE EST CELUI DE LA DEVISE DU MAGASIN (`formatMoney` le lit) : le forcer à « € » mentait sous un magasin en francs.
const euro = (cents: number) => formatMoney(cents, { thousandsSep: ' ' }); // « 1 299,00 € », comme la maquette
const coefTexte = (cents: number | null) => (cents == null ? '—' : `×${formatMinor(cents)}`);
const margeTexte = (bp: number | null) => (bp == null ? 'N/C' : `${formatMinor(bp, { scale: 2, displayScale: 1 })} %`);



export function Prix() {
  const [onglet, setOnglet] = useState<'grille' | 'coefficients'>('grille');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<ArticleModel[] | null>(null);
  const [total, setTotal] = useState(0);
  const [suppliers, setFournisseurs] = useState<ReferentialSupplier[] | null>(null);
  const [marquesOrphelines, setMarquesOrphelines] = useState<MarqueDuReferentiel[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [echecReferentiel, setEchecReferentiel] = useState<string | null>(null);
  /**
   * Les saisies en cours, par modèle — LA FRAPPE BRUTE, jamais des centimes.
   * ⚠️ La première version stockait le montant parsé et re-rendait l'input reformaté à chaque
   * frappe : taper « 6 » affichait « 6,00 », et « 69,99 » devenait insaisissable. Le champ garde
   * exactement ce que l'opérateur tape ; c'est la DÉRIVATION qui parse, via `parseMinor`.
   */
  const [saisies, setSaisies] = useState<Record<string, string>>({});
  const [envoi, setEnvoi] = useState(false);
  const [masseOuverte, setMasseOuverte] = useState(false);
  /** La fiche ouverte depuis la grille — montée ICI, pas une navigation (cf. l'en-tête). */
  const [record, setFiche] = useState<ArticleSheetLine | null>(null);
  /** N° de la dernière requête partie — une réponse en retard ne doit pas écraser une plus récente. */
  const seq = useRef(0);

  const load = useCallback(async (recherche: string) => {
    const id = ++seq.current;
    try {
      // Tri par NOM : la grille se parcourt à l'œil, un ordre de mise à jour y serait illisible.
      const page = await api.articles({ page: 0, size: LIGNES_MAX, sort: 'designation', dir: 'asc', q: recherche });
      if (seq.current !== id) return; // une frappe plus récente a déjà sa requête en vol
      setRows(page.rows);
      setTotal(page.total);
      setFailure(null);
    } catch (e) {
      if (seq.current !== id) return;
      setRows(null);
      setFailure(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    // ⚠️ Une frappe = une requête serait une requête par lettre : on laisse retomber la saisie.
    const timer = setTimeout(() => { void load(q); }, 250);
    return () => clearTimeout(timer);
  }, [q, load]);

  // ⚠️ LE RÉFÉRENTIEL SE LIT AU MONTAGE ET APRÈS UNE ÉCRITURE, jamais à chaque frappe de recherche :
  // sa requête matérialise les paires article×fournisseur du magasin ENTIER.
  const chargerReferentiel = useCallback(async () => {
    try {
      const r = await api.referentielSuppliers();
      setFournisseurs(r.rows);
      setMarquesOrphelines(r.orphanBrands);
      setEchecReferentiel(null);
    } catch (e) {
      setEchecReferentiel(e instanceof Error ? e.message : String(e));
    }
  }, []);
  useEffect(() => { void chargerReferentiel(); }, [chargerReferentiel]);

  /**
   * Écrit la clause d'un fournisseur — par le PRODUCTEUR, exactement comme l'écran Référentiels
   * (même route, même identité par code normalisé : une seule voie d'écriture pour une même clause).
   * Puis relit le référentiel ET la grille : la colonne « Cible » vient de changer.
   */
  const ecrireCoefficient = useCallback(async (entite: 'brand' | 'supplier', code: string, label: string, bp: number | null) => {
    try {
      await api.createReferentielEntry(entite, { code, coefficientBp: bp }, `coef:${entite}:${code}`);
      annoncerCoefficient(label, bp);
      await Promise.all([chargerReferentiel(), load(q)]);
      return true;
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e), 'err');
      await chargerReferentiel();
      return false;
    }
  }, [chargerReferentiel, load, q]);

  const reste = Math.max(0, total - (rows?.length ?? 0));

  // Le bilan des saisies : combien de lignes changées, combien d'illisibles — mesuré sur les lignes
  // affichées, à chaque rendu (60 lignes d'arithmétique entière : rien à mémoïser).
  const envois: { modelKey: string; name: string; cents: number }[] = [];
  let illisibles = 0;
  for (const a of rows ?? []) {
    const brut = saisies[a.modelKey];
    if (brut === undefined) continue;
    const pv = litSaisie(brut, a.priceMinCents);
    if (pv.etat === 'valide') envois.push({ modelKey: a.modelKey, name: a.name, cents: pv.cents! });
    else if (pv.etat === 'illisible') illisibles += 1;
  }

  /**
   * Enregistre les lignes valides — un appel par modèle (ses deux prix ensemble), par la route qui
   * pose LA MÊME valeur sur toutes les déclinaisons.
   * ⚠️ FAIL-FAST ET COMPTE RÉEL : si une écriture échoue, on s'arrête, on dit laquelle, et les
   * saisies GARDENT ce qui n'est pas parti. Annoncer « enregistré » sur un lot à moitié écrit ferait
   * croire la caisse à jour d'un prix qu'elle ne recevra jamais (§23.0).
   * ⚠️ LE RAPPORT FAIT FOI, PAS L'ABSENCE D'ERREUR : un modèle disparu entre l'affichage et le clic (retrait EDI, geste
   *    d'un collègue) répond 200 avec ZÉRO modèle trouvé — rien n'est écrit, et le compter « enregistré » serait le
   *    même mensonge (Forme B).
   * ⚠️ LA GRILLE SE RELIT DANS LES DEUX CAS : après une interruption, les lignes déjà écrites ont perdu leur saisie —
   *    sans relecture elles réafficheraient leur ANCIEN prix, l'état d'avant un travail qui a eu lieu.
   */
  async function save() {
    setEnvoi(true);
    const restants = { ...saisies };
    let written = 0;
    try {
      for (const { modelKey, name, cents } of envois) {
        // ⚠️ CLÉ D'ENVOI, jamais de contenu : reposer le même prix après un aller-retour est une
        //    intention légitime et distincte (défaut du 2026-08-17). Les montants partent en UNITÉ
        //    MINEURE sérialisée, comme depuis la fiche.
        const r = await api.setArticleFields(modelKey, { salePriceInclTaxCents: String(cents) }, `prix-grille-${modelKey}-${crypto.randomUUID()}`);
        if (r.models === 0) throw new Error(t('« {nom} » n’est plus au catalogue — rien n’a été écrit.', { nom: name }));
        delete restants[modelKey];
        written += 1;
      }
      setSaisies({});
      showToast(t('{n} ligne enregistrée', { n: written }), 'ok');
    } catch (e) {
      setSaisies(restants);
      showToast(t('Écriture interrompue — {faites}, {restantes} : {v}', {
        faites: t('{n} enregistrée', { n: written }),
        restantes: t('{n} restante', { n: envois.length - written }),
        v: e instanceof Error ? e.message : String(e),
      }), 'err');
    } finally {
      setEnvoi(false);
    }
    await load(q);
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 250 }}>
          <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>
            
            {t('Prix & marges')}
          </h1>
          <p style={{ margin: '3px 0 0', fontSize: '13.5px', color: 'var(--ink-500)' }}>
            
            {t('Tout le catalogue en une vue : coût, prix, marge, et l’écart au coefficient convenu avec chaque marque.')}
          </p>
        </div>
        {/* Le geste PRINCIPAL de la page — celui qui dit à quoi elle sert. */}
        <button
          type="button"
          onClick={() => setMasseOuverte(true)}
          style={{
            height: 40, padding: '0 16px', border: '1px solid var(--teal-400)', borderRadius: 'var(--r-sm)',
            background: 'var(--paper)', color: 'var(--teal-800)', fontSize: 'var(--t-title)', fontWeight: 700,
            display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', flexShrink: 0,
          }}
        >
          <Icon name="percent" size={15} />  {t('Modification de masse')}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--ink-200)', marginBottom: 20, overflowX: 'auto' }}>
        {([['grille', t('Prix & marges')], ['coefficients', t('Coefficients marque')]] as const).map(([id, label]) => (
          <button aria-pressed={onglet === id}
            key={id}
            type="button"
            onClick={() => setOnglet(id)}
            style={{
              height: 40, padding: '0 15px', border: 'none', background: 'transparent', cursor: 'pointer',
              borderBottom: `2px solid ${onglet === id ? 'var(--teal-500)' : 'transparent'}`,
              color: onglet === id ? 'var(--ink-900)' : 'var(--ink-500)',
              fontSize: 'var(--t-title)', fontWeight: onglet === id ? 700 : 500,
            }}
          >{label}</button>
        ))}
      </div>

      {failure && (
        <div style={{
          marginBottom: 14, padding: '12px 14px', borderRadius: 'var(--r-sm)',
          background: 'var(--rose-bg)', border: '1px solid var(--rose)', fontSize: '12.5px', color: 'var(--ink-800)',
        }}>
          {t('Le catalogue n’a pas pu être lu : {v}', { v: failure })}
        </div>
      )}

      {onglet === 'grille' ? (
        <Grille
          q={q} setQ={setQ} rows={rows} enEchec={failure !== null} reste={reste}
          saisies={saisies} setSaisies={setSaisies}
          touches={envois.length} illisibles={illisibles} envoi={envoi} onEnregistrer={save}
          onOuvrirFiche={setFiche}
          onDefinirCible={() => setOnglet('coefficients')}
        />
      ) : (
        <Coefficients rows={suppliers} orphelines={marquesOrphelines} failure={echecReferentiel} onWrite={ecrireCoefficient} />
      )}

      {/* ⚠️ MONTÉE HORS DE LA GRILLE, comme dans la liste : la fiche survit au rechargement des
          lignes. `offresInitiales` absent = elle charge ses référentiels elle-même (son repli prévu). */}
      <PanneauMassePrix
        open={masseOuverte}
        onClose={() => setMasseOuverte(false)}
        onApplied={() => { void load(q); }}
      />
      <ArticleSheet
        line={record}
        onClose={() => setFiche(null)}
        // La copie d'un article s'ouvre à la place de la fiche, comme depuis la liste des articles.
        onOpenModel={setFiche}
        /* La page se relit après une écriture : les lignes viennent de changer. */
        onChanged={() => { void load(q); }}
      />
    </div>
  );
}

function Grille({
  q, setQ, rows, enEchec, reste, saisies, setSaisies, touches, illisibles, envoi, onEnregistrer, onOuvrirFiche, onDefinirCible,
}: {
  q: string; setQ: (v: string) => void; rows: ArticleModel[] | null; enEchec: boolean; reste: number;
  saisies: Record<string, string>; setSaisies: (v: Record<string, string>) => void;
  touches: number; illisibles: number; envoi: boolean; onEnregistrer: () => void;
  onOuvrirFiche: (a: ArticleModel) => void;
  onDefinirCible: () => void;
}) {
  // Le gabarit de la maquette, 34 px compris : la dernière colonne porte le bouton « appliquer le
  // coefficient fournisseur » de chaque ligne.
  // ⚠️ La colonne du prix de vente fait 124 px, et non les 92 de la maquette : le mot unique du champ,
  //    « Prix de vente TTC », ne tient pas en 92 px en capitales espacées — on élargit, on ne raccourcit
  //    jamais le mot.
  const COLONNES = 'minmax(0,1.6fr) 104px 124px 92px 74px 74px 82px 34px';

  return (
    <>
      <div style={{
        background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)',
        padding: '14px 16px', marginBottom: 14,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 9, height: 36, padding: '0 12px',
          borderRadius: 'var(--r-pill)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)',
        }}>
          <Icon name="search" size={15} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('Désignation, code, code-barres, marque…')}
            style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', outline: 'none', fontSize: 'var(--t-lead)' }}
          />
        </div>
      </div>

      {/* ⚠️ LE BANDEAU NE PARAÎT QU'À PARTIR D'UNE MODIFICATION, et il annonce ce qui va se passer
          RÉELLEMENT : les prix descendent aux caisses au prochain contact, pas à l'instant du clic.
          Une saisie ILLISIBLE bloque l'envoi et se compte — enregistrer « ce qu'on peut » laisserait
          croire que tout est parti. */}
      {(touches > 0 || illisibles > 0) && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', marginBottom: 14, flexWrap: 'wrap',
          borderRadius: 'var(--r-sm)', background: 'var(--amber-bg)', border: '1px solid var(--amber-fg)',
        }}>
          <span style={{ flex: 1, minWidth: 220, fontSize: 'var(--t-body)', color: 'var(--ink-800)' }}>
            {illisibles > 0
              ? <Trans k='<1>{lignes}</1> · <2>{saisies}</2> — les prix descendront aux caisses au prochain contact une fois enregistrés.'
                  vars={{ lignes: t('{n} ligne modifiée', { n: touches }), saisies: t('{n} saisie illisible', { n: illisibles }) }}
                  parts={[(x) => <strong>{x}</strong>, (x) => <strong style={{ color: 'var(--rose-fg)' }}>{x}</strong>]} />
              : <Trans k='<1>{lignes}</1> — les prix descendront aux caisses au prochain contact une fois enregistrés.'
                  vars={{ lignes: t('{n} ligne modifiée', { n: touches }) }}
                  parts={[(x) => <strong>{x}</strong>]} />}
          </span>
          <button type="button" className="btn-ghost" disabled={envoi} onClick={() => setSaisies({})}>{t('Tout annuler')}</button>
          <button
            type="button" className="btn-cta"
            disabled={envoi || touches === 0 || illisibles > 0}
            title={illisibles > 0 ? t('Corrigez les saisies illisibles avant d’enregistrer.') : undefined}
            onClick={onEnregistrer}
          >
            {envoi ? t('Enregistrement…') : t('Enregistrer')}
          </button>
        </div>
      )}

      <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 892 }}>
            <div style={{
              display: 'grid', gridTemplateColumns: COLONNES, gap: 9, padding: '11px 16px',
              borderBottom: '1px solid var(--ink-200)', background: 'var(--ink-50)',
              fontSize: 'var(--t-meta)', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--ink-500)',
            }}>
              <span>{t('Article')}</span>
              <span style={{ textAlign: 'right' }}>{fieldLabel('purchasePriceExclTax')}</span>
              <span style={{ textAlign: 'right' }}>{fieldLabel('salePriceInclTax')}</span>
              <span style={{ textAlign: 'right' }}>{t('Dont HT')}</span>
              <span style={{ textAlign: 'right' }}>{t('Coef.')}</span>
              <span style={{ textAlign: 'right' }}>{t('Cible')}</span>
              <span style={{ textAlign: 'right' }}>{t('Marge')}</span>
              <span aria-hidden="true" />
            </div>

            {rows === null ? (
              // ⚠️ En échec, la grille ne dit pas « Lecture… » : la lecture a eu lieu, elle a échoué,
              // et le bandeau au-dessus porte le motif réel (§23.0).
              <p style={{ margin: 0, padding: '16px', fontSize: 'var(--t-lead)', color: 'var(--ink-500)' }}>
                {enEchec ? t('Relevé indisponible — le motif est au-dessus.') : t('Lecture…')}
              </p>
            ) : rows.length === 0 ? (
              <p style={{ margin: 0, padding: 30, textAlign: 'center', fontSize: 'var(--t-lead)', color: 'var(--ink-500)' }}>{t('Aucun article ne correspond à cette recherche.')}</p>
            ) : (
              rows.map((a) => (
                <LigneGrille
                  key={a.modelKey} a={a} colonnes={COLONNES}
                  brut={saisies[a.modelKey]}
                  onSaisie={(texte) => {
                    const suivant = { ...saisies };
                    if (texte === null) delete suivant[a.modelKey];
                    else suivant[a.modelKey] = texte;
                    setSaisies(suivant);
                  }}
                  onOpen={() => onOuvrirFiche(a)}
                  onDefinirCible={onDefinirCible}
                />
              ))
            )}
          </div>
        </div>

        {reste > 0 && (
          <div style={{
            padding: '10px 16px', borderTop: '1px solid var(--ink-100)', background: 'var(--ink-50)',
            fontSize: 'var(--t-dense)', color: 'var(--ink-500)',
          }}>
            {t('+ {n} article — affinez la recherche pour les atteindre.', { n: reste })}
          </div>
        )}
      </div>

      <p style={{ margin: '10px 2px 0', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', lineHeight: 1.5 }}>
        <Trans k='Le <1>coefficient</1> va du prix d’achat HT au prix de vente TTC : coef = PV TTC ÷ prix d’achat HT. La colonne <2>Cible</2> est la clause de la <3>marque</3> (héritée de son groupe fournisseur si elle n’en déclare pas) — une cible, pas une contrainte. La marge est ici <4>théorique</4> (sur le tarif) : celle de la fiche, comptable, se calcule sur le PMP. Le nom d’un article ouvre sa fiche — dérogations par déclinaison et historique de prix s’y trouvent.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></p>
    </>
  );
}

/** Une ligne de la grille — le prix de vente saisissable, quatre valeurs dérivées, le motif d'un blocage. */
function LigneGrille({
  a, colonnes, brut, onSaisie, onOpen, onDefinirCible,
}: {
  a: ArticleModel; colonnes: string; brut: string | undefined;
  onSaisie: (texte: string | null) => void; onOpen: () => void; onDefinirCible: () => void;
}) {
  const meta = metaDe(a);
  const pv = litSaisie(brut, a.priceMinCents);
  // Les indicateurs suivent la SAISIE EN COURS : c'est ce qui permet de viser un coefficient en
  // tapant un prix, sans enregistrer pour voir le résultat.
  const { ind, paFine, paTheorique } = indicateursLigne(a, meta, pv.cents);
  const variance = ecartALaCible(ind?.coeffCents ?? null, meta.cibleCents);

  // Le geste de la maquette : appliquer d'un clic le coefficient convenu — le prix qui en résulte
  // remplit la saisie, rien n'est écrit avant « Enregistrer ». ⚠️ Le coefficient du CONTRAT, en points de base :
  // l'arrondir au centième avant de multiplier déplacerait le prix.
  const prixCible = meta.blocage === null && !paTheorique && meta.vatBp != null
    ? ttcFromCoeffBp(paFine, meta.cibleBp)
    : null;

  const touchee = pv.etat !== 'intact';

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: colonnes, gap: 9, padding: '9px 16px', alignItems: 'center',
      borderBottom: '1px solid var(--ink-100)',
      background: touchee ? 'var(--teal-100)' : 'transparent',
    }}>
      <div style={{ minWidth: 0 }}>
        {/* Le nom OUVRE LA FICHE — même geste que la liste d'articles, et c'est par elle que
            l'historique de prix et les dérogations par déclinaison sont à portée. */}
        <button
          type="button" onClick={onOpen} title={t('Ouvrir la fiche article')}
          style={{
            display: 'block', maxWidth: '100%', padding: 0, border: 'none', background: 'transparent',
            fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-900)', cursor: 'pointer', textAlign: 'left',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
        >
          {a.name}
        </button>
        <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', fontFamily: 'var(--font-mono)' }}>
          {/* ⚠️ LA MARQUE, pas le fournisseur (recadrage 2026-08-29 : le fournisseur est un GROUPE
              générique, la marque est ce que portent les articles — 99,9 % des articles repris en ont
              une, zéro un fournisseur). Un fournisseur sans marque vend sous son propre nom : repli. */}
          {a.articleCode ?? a.gencode ?? a.modelKey}{(a.brand ?? a.supplier) ? ` · ${a.brand ?? a.supplier}` : ''}
        </div>
      </div>

      {/* ⚠️ PRIX D'ACHAT EN LECTURE — décision utilisateur 2026-08-29, qui STATUE la divergence
          avec la maquette (elle l'édite) : le prix d'achat se fige aux réceptions (BL) et aux fiches,
          cette grille sert le prix de VENTE. En corner il est THÉORIQUE (« ≈ », déduit du contrat). */}
      <span
        title={paTheorique ? t('Prix d’achat théorique — déduit du coefficient contractuel (régime corner).') : t('Le prix d’achat se règle aux réceptions (BL) et sur la fiche article.')}
        style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-dense)', color: paFine != null ? 'var(--ink-700)' : 'var(--ink-400)' }}
      >
        {paFine != null ? `${paTheorique ? '≈ ' : ''}${euro(fineEnCentimes(paFine))}` : 'N/C'}
      </span>

      {meta.blocage ? (
        // ⚠️ ON NE PROPOSE PAS UN CHAMP QU'ON NE PEUT PAS HONORER : la ligne dit ce qui l'empêche et
        //    ouvre la fiche, où la dérogation par déclinaison se règle vraiment.
        <button
          type="button" onClick={onOpen} title={meta.blocage}
          style={{
            gridColumn: 'span 6', justifySelf: 'stretch', height: 30, border: '1px dashed var(--ink-300)',
            borderRadius: 'var(--r-ctrl)', background: 'transparent', color: 'var(--ink-500)',
            fontSize: 'var(--t-note)', cursor: 'pointer', padding: '0 8px',
          }}
        >{t('{blocage} — ouvrir la fiche', { blocage: meta.blocage })}</button>
      ) : (
        <>
          <input
            // ⚠️ AUCUN TEXTE VOISIN NE LE PRÉSENTE (une cellule de grille) : son nom dit le champ ET la ligne, sinon soixante
            //    champs s’annonceraient tous « Prix de vente TTC ».
            aria-label={t('{v} de {article}', { v: fieldLabel('salePriceInclTax'), article: a.name })}
            inputMode="decimal"
            // La frappe reste TELLE QUELLE ; le format ne s'applique qu'aux valeurs non touchées.
            value={brut ?? formatMinor(a.priceMinCents)}
            onChange={(e) => onSaisie(e.target.value)}
            onBlur={() => {
              // Quitter le champ revenu au prix d'origine retire la ligne du bilan — sinon « 1 ligne
              // modifiée » resterait affiché sur une valeur identique à celle en base.
              if (pv.etat === 'intact' && brut !== undefined) onSaisie(null);
            }}
            title={pv.etat === 'illisible' ? pv.motif : undefined}
            style={{
              height: 30, padding: '0 8px', borderRadius: 'var(--r-ctrl)',
              border: `1px solid ${pv.etat === 'illisible' ? 'var(--rose)' : 'var(--ink-200)'}`,
              width: '100%', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-body)',
              background: 'var(--paper)',
            }}
          />
          <span style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>
            {ind?.htCents == null ? '—' : euro(ind.htCents)}
          </span>
          <span
            title={paTheorique ? t('Coefficient contractuel — en corner il est la donnée, pas un dérivé.') : undefined}
            style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-800)' }}
          >
            {coefTexte(ind?.coeffCents ?? null)}
          </span>
          {/* ⚠️ SANS CIBLE, LA CELLULE DIT OÙ ELLE SE DÉFINIT (retour utilisateur 2026-08-29 :
              « où est-ce qu'on saisit la cible ?? ») : un tiret muet obligeait à deviner l'onglet. */}
          {meta.cibleCents == null ? (
            <button
              type="button" onClick={onDefinirCible}
              title={t('Aucune clause pour la marque de cet article — cliquez pour la définir (onglet Coefficients marque).')}
              style={{
                justifySelf: 'end', height: 22, padding: '0 7px', border: '1px dashed var(--ink-300)',
                borderRadius: 'var(--r-ctrl)', background: 'transparent', color: 'var(--ink-400)',
                fontSize: 'var(--t-meta)', cursor: 'pointer',
              }}
            >{t('définir')}</button>
          ) : (
            <span style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-dense)', color: 'var(--ink-400)' }}>
              {coefTexte(meta.cibleCents)}
            </span>
          )}
          {/* ⚠️ L'écart à la cible se signale SUR LA MARGE, en pastille — c'est la forme de la
              maquette. Et « N/C » n'est pas « 0 % » : sans coût connu il n'y a pas de marge, l'afficher
              à zéro se lirait comme une vente à prix coûtant. */}
          <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 'var(--t-body)' }}>
            {variance === 'low' ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 8px', borderRadius: 'var(--r-pill)', background: 'var(--rose-bg)', color: 'var(--rose-fg)', fontWeight: 700 }}>{margeTexte(ind?.margeBp ?? null)}</span>
            ) : variance === 'high' ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 8px', borderRadius: 'var(--r-pill)', background: 'var(--teal-100)', color: 'var(--teal-800)', fontWeight: 700 }}>{margeTexte(ind?.margeBp ?? null)}</span>
            ) : (
              <span style={{ fontWeight: 600, color: 'var(--ink-700)' }}>{margeTexte(ind?.margeBp ?? null)}</span>
            )}
          </span>
          <span style={{ textAlign: 'right' }}>
            {prixCible != null && (
              <button
                type="button"
                aria-label={t('Appliquer le coefficient fournisseur à « {article} »', { article: a.name })}
                title={t('Appliquer le coefficient fournisseur ({coef}) — remplit {prix}, rien n’est écrit avant Enregistrer.', { coef: coefTexte(meta.cibleCents), prix: euro(prixCible) })}
                onClick={() => onSaisie(formatMinor(prixCible))}
                style={{
                  width: 28, height: 28, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
                  background: 'var(--paper)', color: 'var(--ink-600)', cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Icon name="target" size={14} />
              </button>
            )}
          </span>
        </>
      )}
    </div>
  );
}

/** Une ligne de l'onglet : une MARQUE, son groupe, et la clause qui s'applique. */
interface LigneMarque {
  /** `brand` ou `supplier` — commande l'entité que l'écriture vise. */
  entite: 'brand' | 'supplier';
  code: string;
  label: string;
  groupe: string | null;
  models: number;
  coefficientBp: number | null;
  /** La clause du GROUPE, en filigrane : c'est elle qui commande quand la marque n'en déclare pas. */
  heritageBp: number | null;
  /** Un fournisseur sans marque déclarée vend sous son propre nom — sa ligne le dit. */
  estGroupeSeul: boolean;
}

/**
 * L'onglet des coefficients — PAR MARQUE, l'entité opérationnelle (recadrage utilisateur 2026-08-29 :
 * « fournisseur est un groupe générique », la marque est ce que portent les articles — mesuré :
 * 379 291 articles sur 379 682 chez MEN FACTORY portent une marque, zéro un fournisseur).
 *
 * ⚠️ L'ÉCRITURE PASSE PAR LE PRODUCTEUR, exactement comme l'écran Référentiels (mêmes entités
 * `brand`/`supplier`, même identité par code) : deux voies d'écriture pour une même clause
 * finiraient par diverger. Vider une clause de marque la remet en HÉRITAGE du groupe.
 * ⚠️ UN FOURNISSEUR SANS MARQUE vend sous son propre nom (« fournisseur = marque », doctrine du
 * référentiel) : sa ligne apparaît ici comme une marque, et l'écriture vise l'entité `supplier`.
 */
function Coefficients({ rows, orphelines, failure, onWrite }: {
  rows: ReferentialSupplier[] | null;
  orphelines: MarqueDuReferentiel[];
  failure: string | null;
  /** Rend `true` quand la clause est écrite, `false` sur un refus (dit par un message). */
  onWrite: (entite: 'brand' | 'supplier', code: string, label: string, bp: number | null) => Promise<boolean>;
}) {
  if (failure !== null) {
    return (
      <p role="alert" style={{ fontSize: 'var(--t-lead)', color: 'var(--danger-ink)' }}>
        {t('Le référentiel n’a pas pu être lu : {failure}', { failure })}
      </p>
    );
  }
  if (rows === null) return <p style={{ fontSize: 'var(--t-lead)', color: 'var(--ink-500)' }}>{t('Lecture…')}</p>;

  // La liste à plat : les marques de chaque groupe, les groupes sans marque (= leur propre marque),
  // et les marques orphelines — rien n'est caché.
  const lines: LigneMarque[] = [
    ...rows.flatMap((s): LigneMarque[] => (s.brands.length === 0
      ? [{ entite: 'supplier', code: s.code, label: s.label, groupe: s.label, models: s.models, coefficientBp: s.coefficientBp, heritageBp: null, estGroupeSeul: true }]
      : s.brands.map((b): LigneMarque => ({ entite: 'brand', code: b.code, label: b.label, groupe: s.label, models: b.models, coefficientBp: b.coefficientBp, heritageBp: s.coefficientBp, estGroupeSeul: false })))),
    ...orphelines.map((b): LigneMarque => ({ entite: 'brand', code: b.code, label: b.label, groupe: null, models: b.models, coefficientBp: b.coefficientBp, heritageBp: null, estGroupeSeul: false })),
  ].sort((a, b) => a.label.localeCompare(b.label, 'fr'));

  return (
    <>
      <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: 'minmax(0,1.2fr) minmax(0,1fr) 90px 120px', gap: 9, padding: '11px 16px',
          borderBottom: '1px solid var(--ink-200)', background: 'var(--ink-50)',
          fontSize: 'var(--t-meta)', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--ink-500)',
        }}>
          <span>{fieldLabel('brand')}</span>
          <span>{t('Groupe ({v})', { v: fieldLabel('supplier').toLowerCase() })}</span>
          <span style={{ textAlign: 'right' }}>{t('Articles')}</span>
          <span style={{ textAlign: 'right' }}>{t('Coefficient')}</span>
        </div>
        {lines.length === 0 && (
          <p style={{ margin: 0, padding: 30, textAlign: 'center', fontSize: 'var(--t-lead)', color: 'var(--ink-500)' }}>
            
            {t('Aucune marque — le référentiel se remplit depuis les Référentiels, par la reprise ou par l’EDI.')}
          </p>
        )}
        {lines.map((l) => (
          <div key={`${l.entite}:${l.code}`} style={{
            display: 'grid', gridTemplateColumns: 'minmax(0,1.2fr) minmax(0,1fr) 90px 120px', gap: 9,
            padding: '10px 16px', alignItems: 'center', borderBottom: '1px solid var(--ink-100)',
          }}>
            <span style={{ fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 8 }}>
              {l.label}
              {l.estGroupeSeul && (
                <span style={{ height: 18, padding: '0 7px', borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', color: 'var(--ink-600)', fontSize: 'var(--t-micro)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
                  
                  {t('fournisseur = marque')}
                </span>
              )}
            </span>
            <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {l.groupe ?? '—'}
            </span>
            <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 'var(--t-body)', color: 'var(--ink-600)' }}>
              {l.models}
            </span>
            <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
              {/* L'HÉRITAGE EN FILIGRANE : une case vide avec « 2,50 » en placeholder dit « la clause
                  du groupe commande » — vide sans repère laisserait croire qu'aucune ne s'applique. */}
              <ChampCoef
                valeur={coefAffiche(l.coefficientBp)}
                placeholder={coefAffiche(l.heritageBp) || '—'}
                heading={l.entite === 'brand'
                  ? t('Coefficient contractuel de la marque — vide : hérite de son groupe (fournisseur)')
                  : t('Coefficient contractuel du fournisseur (qui vend sous son propre nom)')}
                nom={t('Coefficient de {v}', { v: l.label })}
                largeur={72} hauteur={30} taille="12.5px" couleur="var(--ink-900)"
                onValidate={(v) => {
                  const bp = coeffInput(v);
                  if (bp === undefined) { showToast(t('Coefficient attendu, par exemple « 2,50 ».'), 'warn'); return Promise.resolve(false); }
                  if (bp === l.coefficientBp) return;
                  return onWrite(l.entite, l.code, l.label, bp);
                }}
              />
            </span>
          </div>
        ))}
      </div>
      <p style={{ margin: '10px 2px 0', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', lineHeight: 1.5 }}>
        <Trans k='La clause d’une <1>marque</1> commande la colonne « Cible » de la grille ; vide, elle <2>hérite de son groupe</2> (fournisseur), dont la clause se règle aux Référentiels. Une cible est une référence de négociation — jamais un blocage de saisie.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></p>
    </>
  );
}
